import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import AuthContainer from './components/AuthContainer';
// import SubscriptionModal from './components/SubscriptionModal';
import PricingPlansModal from './components/PricingPlansModal';
import ShopDetailsModal from './components/ShopDetailsModal';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ProductList from './components/ProductList';
import CategoryList from './components/CategoryList';
import OrderList from './components/OrderList';
import QRTableList from './components/QRTableList';
import StoreFrontend from './components/StoreFrontend';
import RestaurantProfileComponent from './components/RestaurantProfile';
import Settings from './components/Settings';
import StaffList from './components/StaffList';
import SEOSettings from './components/SEOSettings';
import { getStaffRoleFromToken } from './utils/staffRoleUtils';
import FeedbackChat from './components/FeedbackChat';
import FeedbackSubmit from './components/FeedbackSubmit';
import NotificationManager from './components/NotificationManager';
import { setUnauthorizedHandler } from './services/api';
import { isRestaurantDetailsMissing } from './utils/restaurantUtils';

const AppContent: React.FC = () => {
  const { user, subscription, isLoading, logout, hasBasicPlan, hasAnyPlan, restaurantDetails, loadRestaurantDetails } = useAuth();
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('selectedTab') || 'products';
  });
  // const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isMandatoryModal, setIsMandatoryModal] = useState(false);
  const [showShopDetailsModal, setShowShopDetailsModal] = useState(false);

  // Set up unauthorized handler
  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
    });
  }, [logout]);

  // Check shop details and show mandatory modal if no plan is selected
  useEffect(() => {
    if (!user || isLoading) {
      return;
    }

    // Wait for restaurantDetails to be loaded (not null and has actual data)
    // Check if restaurantDetails is an object with properties (not just null/undefined)
    const hasRestaurantDetails = restaurantDetails !== null && typeof restaurantDetails === 'object';

    if (!hasRestaurantDetails) {
      return;
    }

    // First check if restaurant details are missing
    if (isRestaurantDetailsMissing()) {
      setShowShopDetailsModal(true);
      setShowPricingModal(false);
      setIsMandatoryModal(false);
      return;
    }

    // Only check plan after restaurant details are loaded
    const planStatus = restaurantDetails?.plan;
    const hasPlan = planStatus === 'basic' || planStatus === 'pro';
    const noPlan = !planStatus || planStatus === 'no active plan' || planStatus === 'no_plan';

    // If no plan is selected, show mandatory pricing modal
    if (noPlan || !hasPlan) {
      setIsMandatoryModal(true);
      setShowPricingModal(true);
    } else {
      setIsMandatoryModal(false);
      
      // User has a plan, check if they want to upgrade (only show if they have basic and want to see pro)
      const shouldShowUpgradeModal = localStorage.getItem('showUpgradeModal') === 'true';
      if (shouldShowUpgradeModal) {
        setShowPricingModal(true);
        setIsMandatoryModal(false);
        localStorage.removeItem('showUpgradeModal');
      } else {
        setShowPricingModal(false);
      }
    }
  }, [user, restaurantDetails, isLoading]);

  const handleClosePricingModal = () => {
    // Only allow closing if it's not mandatory
    if (!isMandatoryModal) {
      setShowPricingModal(false);
      localStorage.setItem('pricingModalShown', 'true');
    }
  };

  const handlePlanSelect = async (plan: 'basic' | 'pro') => {
    try {
      // Get current restaurant details from localStorage
      const restaurantDetails = localStorage.getItem('restaurantDetails');
      if (restaurantDetails) {
        const details = JSON.parse(restaurantDetails);
        const updatedDetails = { ...details, plan };
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedDetails));
      }
      
      // Close modal after selection
      setShowPricingModal(false);
      setIsMandatoryModal(false);
      
      // Reload the page after plan selection
      setTimeout(() => {
        window.location.reload();
      }, 1000); // Wait 1 second before reload
    } catch (error) {
      console.error('Error selecting plan:', error);
    }
  };

  const handleShopDetailsSuccess = async () => {
    setShowShopDetailsModal(false);
    // Reload restaurant details from API to get latest data including plan status
    await loadRestaurantDetails(true);
    
    // After reloading, wait a moment for state to update, then check plan status
    // Get the latest restaurantDetails from localStorage after reload
    setTimeout(() => {
      try {
        const storedDetails = localStorage.getItem('restaurantDetails');
        if (storedDetails) {
          const details = JSON.parse(storedDetails);
          const planStatus = details?.plan;
          const hasPlan = planStatus === 'basic' || planStatus === 'pro';
          
          // If no plan is set, show pricing modal immediately
          if (!hasPlan) {
            setIsMandatoryModal(true);
            setShowPricingModal(true);
          }
        } else {
          // No details found, show pricing modal
          setIsMandatoryModal(true);
          setShowPricingModal(true);
        }
      } catch (error) {
        console.error('Error checking plan status after shop details update:', error);
        // On error, show pricing modal to be safe
        setIsMandatoryModal(true);
        setShowPricingModal(true);
      }
    }, 500);
  };

  const handleCloseShopDetailsModal = () => {
    // For now, we'll allow closing the modal
    // In a production app, you might want to make this mandatory
    setShowShopDetailsModal(false);
  };

  // Save selected tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedTab', currentPage);
  }, [currentPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Listen for notification navigation events
  useEffect(() => {
    const triggerOrdersRefresh = () => {
      window.dispatchEvent(new CustomEvent('ordersRefreshRequested'));
    };

    const isNewOrderNotification = (data: any) =>
      data?.title === 'New Order Received!';

    const handleNotificationNavigation = (event: CustomEvent) => {
      const { page, data } = event.detail || {};
      if (page && page !== currentPage) {
        setCurrentPage(page);
      }

      if (isNewOrderNotification(data)) {
        if (currentPage === 'orders') {
          triggerOrdersRefresh();
        } else {
          localStorage.setItem('pendingOrdersRefresh', '1');
        }
      }
    };

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NOTIFICATION_NAVIGATION') {
        const { page, data } = event.data as { page?: string; data?: any };
        
        if (page && page !== currentPage) {
          setCurrentPage(page);
        }

        if (isNewOrderNotification(data)) {
          if (currentPage === 'orders') {
            triggerOrdersRefresh();
          } else {
            // Fallback path for background notification clicks:
            // switch to orders and trigger refresh once page is active.
            localStorage.setItem('pendingOrdersRefresh', '1');
          }
        }

        // If waiter/staff call notification clicked in background, trigger modal via global event
        try {
          if (
            data &&
            data.page === 'notification' &&
            (data.to === 'waiter' || data.to === 'staff') &&
            (data.reason === 'bill' || data.reason === 'other') &&
            data.table
          ) {
            console.log(data)
            const waiterEvent = new CustomEvent('waiterCall', {
              detail: {
                table: String(data.table),
                reason: String(data.reason),
                raw: data
              }
            });
            window.dispatchEvent(waiterEvent);
          }
        } catch (e) {
          console.warn('Failed to dispatch waiterCall from SW message', e);
        }
      }
    };

    const handleForegroundNotificationReceived = (event: CustomEvent) => {
      const data = event.detail?.data;
      if (!isNewOrderNotification(data)) return;

      if (currentPage === 'orders') {
        triggerOrdersRefresh();
      } else {
        localStorage.setItem('pendingOrdersRefresh', '1');
      }
    };

    // Global message handler that persists across re-renders
    // const globalMessageHandler = (event: MessageEvent) => {
    //   console.log('Received message from global message handler:', event.data);
    //   if (event.data && event.data.type === 'NOTIFICATION_NAVIGATION') {
    //     const { page } = event.data;
        
    //     // Use a more direct approach to update the page
    //     if (page) {
    //       // Store in localStorage and trigger a custom event
    //       localStorage.setItem('pendingNavigation', JSON.stringify({ page, timestamp: Date.now() }));
    //       window.dispatchEvent(new CustomEvent('pendingNavigation', { detail: { page } }));
    //     }
    //   }
    // };

    // Check for localStorage navigation data (from service worker)
    // const checkNotificationNavigation = () => {
    //   // Also check for pending navigation
    //   const pendingNav = localStorage.getItem('pendingNavigation');
    //   if (pendingNav) {
    //     try {
    //       const { page, timestamp } = JSON.parse(pendingNav);
    //       // Only process if the data is recent (within last 5 seconds)
    //       if (page && Date.now() - timestamp < 5000) {
    //         if (page !== currentPage) {
    //           setCurrentPage(page);
    //         }
    //         // Clear the pending navigation data
    //         localStorage.removeItem('pendingNavigation');
    //       }
    //     } catch (error) {
    //       console.error('Error parsing pending navigation data:', error);
    //       localStorage.removeItem('pendingNavigation');
    //     }
    //   }
    // };

    // Check immediately
    // checkNotificationNavigation();

    // Check periodically for navigation data
    // const interval = setInterval(checkNotificationNavigation, 1000);

    // Listen for custom events from foreground notifications
    window.addEventListener('notificationNavigation', handleNotificationNavigation as EventListener);
    
    // Listen for messages from service worker (background notifications)
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    window.addEventListener('foregroundNotificationReceived', handleForegroundNotificationReceived as EventListener);
    
    
    // Listen for pending navigation events
    // const handlePendingNavigation = (event: CustomEvent) => {
    //   const { page } = event.detail;
    //   if (page && page !== currentPage) {
    //     setCurrentPage(page);
    //   }
    // };
    // window.addEventListener('pendingNavigation', handlePendingNavigation as EventListener);

    return () => {
      window.removeEventListener('notificationNavigation', handleNotificationNavigation as EventListener);
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      window.removeEventListener('foregroundNotificationReceived', handleForegroundNotificationReceived as EventListener);
    };
  }, [currentPage]);

  // Execute deferred order refresh when user lands on orders page.
  useEffect(() => {
    if (currentPage !== 'orders') return;

    const pendingRefresh = localStorage.getItem('pendingOrdersRefresh') === '1';
    if (pendingRefresh) {
      window.dispatchEvent(new CustomEvent('ordersRefreshRequested'));
      localStorage.removeItem('pendingOrdersRefresh');
    }
  }, [currentPage]);

  // Frontend-only page visibility:
  // - shopOwner/manager can access all pages
  // - everyone else is forced to Orders page only
  useEffect(() => {
    if (!user || isLoading) return;

    // Help Center disabled for everyone (frontend-only)
    if (currentPage === 'submit-feedback') {
      setCurrentPage('orders');
      return;
    }

    const role = getStaffRoleFromToken();
    const canSeeAllPages = role === 'shopOwner' || role === 'manager';
    if (!canSeeAllPages && currentPage !== 'orders') {
      setCurrentPage('orders');
    }
  }, [user, isLoading, currentPage]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!user) {
    return <AuthContainer />;
  }

  // Check if user has active subscription
  const isSubscriptionSkipped = localStorage.getItem('subscription_skipped') === 'true';
  // Modal is hidden on startup - user can access it manually if needed
  // if (!subscription || (subscription.status !== 'active' && !isSubscriptionSkipped)) {
  //   if (!showSubscriptionModal) {
  //     setShowSubscriptionModal(true);
  //   }
  // }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onPageChange={setCurrentPage} onShowSubscriptionModal={() => {
          setShowPricingModal(true);
          setIsMandatoryModal(false);
        }} />;
      case 'products':
        return <ProductList onNavigateToCategories={() => setCurrentPage('categories')} />;
      case 'categories':
        return <CategoryList />;
      case 'orders':
        return <OrderList />;
      case 'tables':
        return <QRTableList onShowSubscriptionModal={() => {
          setShowPricingModal(true);
          setIsMandatoryModal(false);
        }} />;
      case 'profile':
        return <RestaurantProfileComponent />;
      case 'settings':
        return <Settings />;
      case 'staffs':
        return <StaffList />;
      case 'seo':
        return <SEOSettings />;
      case 'store':
        return <StoreFrontend />;
      case 'feedback':
        return <FeedbackChat />;
      case 'submit-feedback':
        return <FeedbackSubmit />;
      default:
        return <Dashboard onPageChange={setCurrentPage} onShowSubscriptionModal={() => {
          setShowPricingModal(true);
          setIsMandatoryModal(false);
        }} />;
    }
  };

  return (
    <>
      <Layout 
        currentPage={currentPage} 
        onPageChange={setCurrentPage}
        onShowSubscriptionModal={() => {
          setShowPricingModal(true);
          setIsMandatoryModal(false);
        }}
      >
        {renderPage()}
      </Layout>

      <NotificationManager />
      
      {/* <SubscriptionModal 
        isOpen={showSubscriptionModal} 
        onClose={() => setShowSubscriptionModal(false)} 
      /> */}
      
      <PricingPlansModal 
        isOpen={showPricingModal} 
        onClose={handleClosePricingModal}
        onPlanSelect={handlePlanSelect}
        showOnlyPro={hasBasicPlan()}
        isMandatory={isMandatoryModal}
      />
      
      <ShopDetailsModal 
        isOpen={showShopDetailsModal} 
        onClose={handleCloseShopDetailsModal}
        onSuccess={handleShopDetailsSuccess}
        userMobile={user?.mobile}
      />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;